import { Box, Container } from '@mui/material';
import { SectionLayout as SectionLayoutType } from '@peerless/data-access/strapi';
import { getColorConversion, formatSectionWidth, formatMediaUrl } from '../utils';
import { ReactNode } from 'react';
import { withErrorBoundary } from '../utils/withErrorBoundary';

interface SectionLayoutProps {
  data: { sectionLayout?: SectionLayoutType | null };
}

interface Props {
  children: ReactNode;
  sectionLayout: SectionLayoutType;
}

export function withSectionLayout<P extends SectionLayoutProps>(
  WrappedComponent: React.ComponentType<P>
) {
  const ErrorBoundComponent = withErrorBoundary(WrappedComponent);

  const SectionLayoutControlled = (props: P & SectionLayoutProps) => {
    const layout: SectionLayoutType = props.data?.sectionLayout ?? {
      backgroundColor: 'white',
      sectionWidth: 'Extra_Large',
      backgroundImage: null,
      paddingTop: 64,
      paddingBottom: 64,
      paddingLeft: 64,
      paddingRight: 64,
    };
    const paddingMobile = layout?.sectionWidth === 'Full_Width' ? 0 : '16px';
    return (
      <SectionLayout sectionLayout={layout}>
        <Container
          maxWidth={
            layout?.sectionWidth === 'Full_Width' ? false : formatSectionWidth(layout?.sectionWidth)
          }
          sx={{
            paddingTop: { xs: `${layout.paddingTop ?? 32}px`, md: `${layout.paddingTop ?? 64}px` },
            paddingBottom: {
              xs: `${layout.paddingBottom ?? 32}px`,
              md: `${layout.paddingBottom ?? 64}px`,
            },
            paddingLeft: { xs: paddingMobile, md: `${layout.paddingLeft ?? 64}px` },
            paddingRight: { xs: paddingMobile, md: `${layout.paddingRight ?? 64}px` },
          }}
        >
          <ErrorBoundComponent {...props} />
        </Container>
      </SectionLayout>
    );
  };

  return SectionLayoutControlled;
}

function SectionLayout({ children, sectionLayout }: Props) {
  const backgroundImage = sectionLayout.backgroundImage
    ? `url(${formatMediaUrl(sectionLayout.backgroundImage)})`
    : undefined;
  return (
    <Box
      sx={{
        backgroundColor: !backgroundImage
          ? getColorConversion(sectionLayout.backgroundColor)
          : undefined,
        backgroundImage: backgroundImage,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'fixed',
        maxWidth: 'false',
        display: sectionLayout.hideSection ? 'none' : 'flex',
        justifyContent: 'center',
      }}
    >
      {children}
    </Box>
  );
}
